import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StatusPill from '../ui/StatusPill';
import { formatJobNumber, sortKey as idSortKey } from '../../utils/displayId';
import { deriveJobStatus, isJobBacklog } from '../../utils/jobStatus';
import { useToast } from '../ui/Toast';
import { useJobCompletion, apiErrorMessage } from '../jobs/JobCompletionDialog';
import useSettingsList from '../../hooks/useSettingsList';
import { crewIdentity } from '../../utils/crewIdentity';

/**
 * IDI-823 (D4): the job's work orders. A non-empty `work_orders` (new Go
 * writes), else `workOrders` (older writes). A plain `||` would return an
 * empty `work_orders` over a full `workOrders`.
 */
function jobWorkOrders(job) {
  if (Array.isArray(job?.work_orders) && job.work_orders.length > 0) return job.work_orders;
  if (Array.isArray(job?.workOrders)) return job.workOrders;
  return [];
}

/**
 * IDI-823 (D4): the Crew cell. Each work order's crew through crewIdentity
 * (as the job page, IDI-472), once each, joined ", "; none → "—". Never
 * `assignedTo` (a salesperson's id on migrated jobs); techs aren't crews.
 */
function crewText(job, crews) {
  const names = [];
  jobWorkOrders(job).forEach((wo) => {
    const id = crewIdentity(wo, crews);
    if (id.assigned && id.name && !names.includes(id.name)) names.push(id.name);
  });
  return names.length ? names.join(', ') : '\u2014';
}

/**
 * DashboardJobsTable — Full-width sortable jobs table (Region 4).
 * REQ-DSH-07: Columns: JOB # / CUSTOMER / TYPE / CREW / STATUS / DUE DATE / kebab menu
 */
export default function DashboardJobsTable({ jobs = [], loading = false, onRefresh }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { requestComplete, dialog: completionDialog } = useJobCompletion();
  const [sortKey, setSortKey] = useState('due_date');
  const [sortDir, setSortDir] = useState('asc');
  const [selectedIds, setSelectedIds] = useState(new Set());
  // IDI-823: above the early returns (hooks order).
  const { items: crews } = useSettingsList('crews');

  // Iter-18: Dashboard shows Not Scheduled + Partially Scheduled jobs ONLY
  // (scheduled/completed jobs aren't dashboard concerns — they live on
  //  the Calendar or the Jobs list respectively).
  // PR-44 (2026-08-18): shared predicate with the dashboard tile.
  // tile === table now holds by construction (see utils/jobStatus.isJobBacklog).
  const activeJobs = jobs.filter(isJobBacklog);

  // Sort
  const sortedJobs = [...activeJobs].sort((a, b) => {
    let aVal, bVal;
    if (sortKey === 'due_date') {
      aVal = a.due_date || a.dueDate || a.scheduled_date || a.scheduledDate || '';
      bVal = b.due_date || b.dueDate || b.scheduled_date || b.scheduledDate || '';
    } else if (sortKey === 'sequential_id') {
      // §1d (2026-08-07): numeric-safe sort. Migrated jobs carry bare
      // integers ("73"), net-new jobs carry padded prefixes ("J-000002").
      // idSortKey normalises so both sort correctly on the same list.
      aVal = idSortKey(formatJobNumber(a));
      bVal = idSortKey(formatJobNumber(b));
    } else if (sortKey === 'customer') {
      aVal = a.customer_name || a.customerName || a.customer || '';
      bVal = b.customer_name || b.customerName || b.customer || '';
    } else if (sortKey === 'type') {
      aVal = a.job_type || a.jobType || a.type || '';
      bVal = b.job_type || b.jobType || b.type || '';
    } else if (sortKey === 'crew') {
      aVal = crewText(a, crews);
      bVal = crewText(b, crews);
    } else if (sortKey === 'status') {
      aVal = a.status || '';
      bVal = b.status || '';
    } else {
      aVal = '';
      bVal = '';
    }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const displayJobs = sortedJobs.slice(0, 10);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h3 style={titleStyle}>Jobs</h3>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} style={skeletonRowStyle} />
          ))}
        </div>
      </div>
    );
  }

  if (activeJobs.length === 0) {
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h3 style={titleStyle}>Jobs</h3>
        </div>
        <div style={emptyStyle}>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-muted)' }}>
            No active jobs
          </p>
        </div>
      </div>
    );
  }

  const columns = [
    { key: 'checkbox', label: '', width: '4%' },
    { key: 'sequential_id', label: 'JOB #', width: '10%' },
    { key: 'customer', label: 'CUSTOMER', width: '20%' },
    { key: 'type', label: 'TYPE', width: '14%' },
    { key: 'crew', label: 'CREW', width: '14%' },
    { key: 'status', label: 'STATUS', width: '12%' },
    { key: 'due_date', label: 'DUE DATE', width: '14%' },
    { key: 'actions', label: '', width: '5%' },
  ];

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h3 style={titleStyle}>Jobs</h3>
        <span style={countBadge}>{activeJobs.length} awaiting scheduling</span>
      </div>

      {/* Table */}
      <div>
        <table style={tableStyle}>
          <thead>
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  onClick={col.key !== 'actions' && col.key !== 'checkbox' ? () => handleSort(col.key) : undefined}
                  style={{
                    ...thStyle,
                    width: col.width,
                    cursor: col.key !== 'actions' && col.key !== 'checkbox' ? 'pointer' : 'default',
                  }}
                >
                  {col.key === 'checkbox' ? (
                    <input
                      type="checkbox"
                      checked={selectedIds.size === displayJobs.length && displayJobs.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds(new Set(displayJobs.map(j => j.id)));
                        } else {
                          setSelectedIds(new Set());
                        }
                      }}
                      style={{ accentColor: 'var(--color-action-primary)' }}
                    />
                  ) : (
                    <>
                      {col.label}
                      {sortKey === col.key && (
                        <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayJobs.map((job, idx) => (
              <JobRow
                key={job.id}
                job={job}
                navigate={navigate}
                isLastRow={idx === displayJobs.length - 1}
                isSelected={selectedIds.has(job.id)}
                onToggleSelect={(id) => {
                  setSelectedIds(prev => {
                    const next = new Set(prev);
                    if (next.has(id)) next.delete(id);
                    else next.add(id);
                    return next;
                  });
                }}
                onRefresh={onRefresh}
                toast={toast}
                requestComplete={requestComplete}
                crews={crews}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* View All link */}
      <div style={{ marginTop: 12, textAlign: 'right' }}>
        <button
          onClick={() => navigate('/jobs')}
          style={viewAllStyle}
        >
          View All Jobs →
        </button>
      </div>
      {completionDialog}
    </div>
  );
}

function JobRow({ job, navigate, isLastRow, isSelected, onToggleSelect, onRefresh, toast, requestComplete, crews }) {
  const displayId = formatJobNumber(job);
  const customer = job.customer_name || job.customerName || job.customer || '—';
  const type = job.job_type || job.jobType || job.type || '—';
  const crew = crewText(job, crews);
  // IDI-139 §2 (2026-09-03, Adrian's ruling): derive from workOrders[].startAt,
  // not stored job.status. isJobBacklog above already routes through
  // deriveJobStatus — this line makes the pill match the filter.
  const status = deriveJobStatus(job);
  const dueDate = job.due_date || job.dueDate || job.scheduled_date || job.scheduledDate || '';

  const formattedDate = dueDate
    ? new Date(dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

  const rowTdStyle = {
    ...tdStyle,
    borderBottom: isLastRow ? 'none' : '1px solid var(--neutral-100)',
  };

  return (
    <tr
      style={{ ...trStyle, cursor: 'pointer' }}
      onClick={() => navigate(`/jobs/${job.id}`)}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--neutral-50)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <td style={rowTdStyle} onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(job.id)}
          style={{ accentColor: 'var(--color-action-primary)' }}
        />
      </td>
      <td style={rowTdStyle}>
        <span style={{ fontWeight: 600, color: 'var(--color-action-primary)' }}>
          {displayId}
        </span>
      </td>
      <td style={rowTdStyle}>{customer}</td>
      <td style={{ ...rowTdStyle, textTransform: 'capitalize' }}>{humanizeType(type)}</td>
      <td style={rowTdStyle}>{crew}</td>
      <td style={rowTdStyle}><StatusPill status={status} size="sm" /></td>
      <td style={rowTdStyle}>{formattedDate}</td>
      <td style={rowTdStyle}>
        <KebabMenu
          onView={() => navigate(`/jobs/${job.id}`)}
          onEdit={() => navigate(`/jobs/${job.id}/edit`)}
          onComplete={async () => {
            // IDI-246: the same confirm as the Job page (lists the open work
            // orders); confirming calls complete_job.
            const res = await requestComplete(job);
            if (!res) return;
            if (res.failed.length) {
              toast?.error?.(apiErrorMessage(res.failed[0].err, 'Failed to mark job complete.'));
              return;
            }
            toast?.success?.('Job marked complete.');
            onRefresh?.();
          }}
        />
      </td>
    </tr>
  );
}

function KebabMenu({ onView, onEdit, onComplete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        style={kebabBtnStyle}
        aria-label="Job actions"
      >
        ⋮
      </button>
      {open && (
        <div style={{
          ...menuStyle,
          ...(btnRef.current && btnRef.current.getBoundingClientRect().bottom > window.innerHeight - 160
            ? { bottom: '100%', marginBottom: 4, top: 'auto', marginTop: 0 }
            : {}),
        }}>
          <button style={menuItemStyle} onClick={() => { onView(); setOpen(false); }}>View</button>
          <button style={menuItemStyle} onClick={() => { onEdit(); setOpen(false); }}>Edit</button>
          {/* IDI-316 row 2 (2026-09-12): real handler now wired (onComplete
              calls jobAPI.updateJob at the call site) — re-enabled per the
              IDI-277 falsifier's own instruction ("becomes live automatically
              when a real handler lands"). */}
          <button style={menuItemStyle} onClick={() => { onComplete(); setOpen(false); }}>Mark Complete</button>
        </div>
      )}
    </div>
  );
}

function humanizeType(type) {
  if (!type) return '—';
  return type.replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Styles
const containerStyle = {
  background: 'var(--color-surface)',
  border: '1px solid var(--neutral-200)',
  borderRadius: "var(--card-radius)",
  padding: 20,
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 12,
};

const titleStyle = {
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
  color: 'var(--color-text-default)',
};

const countBadge = {
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--color-text-muted)',
};

const emptyStyle = {
  padding: '32px 16px',
  textAlign: 'center',
};

const skeletonRowStyle = {
  height: 40,
  borderRadius: 6,
  background: 'linear-gradient(90deg, var(--neutral-100) 25%, var(--neutral-200) 50%, var(--neutral-100) 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.5s infinite',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};

const thStyle = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'var(--color-surface)',
  background: 'var(--table-header-bg)',
  userSelect: 'none',
};

const trStyle = {
  transition: 'background 0.1s',
};

const tdStyle = {
  padding: '10px 10px',
  color: 'var(--color-text-default)',
};

const viewAllStyle = {
  border: 'none',
  background: 'transparent',
  color: 'var(--color-action-primary)',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  padding: '4px 0',
};

const kebabBtnStyle = {
  border: 'none',
  background: 'transparent',
  fontSize: 18,
  cursor: 'pointer',
  padding: '4px 8px',
  borderRadius: 4,
  color: 'var(--color-text-muted)',
  lineHeight: 1,
};

const menuStyle = {
  position: 'absolute',
  right: 0,
  top: '100%',
  marginTop: 4,
  background: 'var(--color-surface)',
  border: '1px solid var(--neutral-200)',
  borderRadius: 8,
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  zIndex: 50,
  minWidth: 140,
  padding: '4px 0',
};

const menuItemStyle = {
  display: 'block',
  width: '100%',
  padding: '8px 14px',
  border: 'none',
  background: 'transparent',
  textAlign: 'left',
  fontSize: 13,
  color: 'var(--color-text-default)',
  cursor: 'pointer',
};
